import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OAuthService } from 'angular-oauth2-oidc';
import { CatalogueTemplate } from 'src/app/models/bot/catalogue';
import { BotGuild } from 'src/app/models/bot/guild';
import { PermissionValue } from 'src/app/models/discord-api/permission';
import { AuthorResubmitCatalogueDialogComponent } from 'src/app/components/author-resubmit-catalogue-dialog/author-resubmit-catalogue-dialog.component';
import { EditCatalogueTemplateDialogComponent } from 'src/app/components/edit-catalogue-template-dialog/edit-catalogue-template-dialog.component';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { GuildService } from 'src/app/services/guild.service';
import { CataloguePreviewService } from 'src/app/services/catalogue-preview.service';
import { EmbedService } from 'src/app/services/embed.service';
import { EmojiParserService } from 'src/app/services/emojiParser.service';
import { PermissionsService } from 'src/app/services/permissions.service';

const GENERAL_LIMIT_ORDER = [
  'max_sign_ups_total',
  'max_sign_ups_per_user',
  'max_sign_ups_per_role',
  'max_sign_ups_per_status',
] as const;

const GENERAL_LIMIT_LABELS: Record<string, string> = {
  max_sign_ups_total: 'Maximum sign ups to event',
  max_sign_ups_per_user: 'Sign ups allowed per user',
  max_sign_ups_per_role: 'Maximum sign ups for all role fields',
  max_sign_ups_per_status: 'Maximum sign ups for all status fields',
};

@Component({
  selector: 'app-template-detail',
  templateUrl: './template-detail.component.html',
  styleUrls: ['./template-detail.component.css'],
})
export class TemplateDetailComponent implements OnInit, OnDestroy {
  @ViewChild('importDialog') importDialog: TemplateRef<any>;

  template: CatalogueTemplate | null = null;
  loading = true;
  importing = false;
  actionBusy = false;
  isBotOwner = false;
  currentUserId: string | null = null;
  guilds: BotGuild[] = [];
  selectedGuildId: string | null = null;
  private dialogRef: MatDialogRef<any> | null = null;

  get isAuthor(): boolean {
    return !!this.template && !!this.currentUserId && this.template.author_id === this.currentUserId;
  }

  get canManageListing(): boolean {
    return this.isAuthor || this.isBotOwner;
  }

  get canEditLive(): boolean {
    return this.isBotOwner && this.template?.status === 'approved';
  }

  get canResubmit(): boolean {
    return this.isAuthor && !this.template?.unlisted_by_admin;
  }

  get canUnlist(): boolean {
    return this.canManageListing && this.template?.status === 'approved';
  }

  get canRelist(): boolean {
    return this.canManageListing && this.template?.status === 'unlisted';
  }

  get relistBlockedByAdmin(): boolean {
    return (
      !!this.template &&
      this.template.status === 'unlisted' &&
      !!this.template.unlisted_by_admin &&
      !this.isBotOwner
    );
  }

  get canDelete(): boolean {
    return this.canManageListing && !!this.template;
  }

  get showReviewerNotes(): boolean {
    return (this.isBotOwner || this.isAuthor) && !!this.template?.notes;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private botService: BotResourceService,
    private previewService: CataloguePreviewService,
    private embedService: EmbedService,
    private oauthService: OAuthService,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
    private guildService: GuildService,
    private emojiParser: EmojiParserService,
    private sanitizer: DomSanitizer,
  ) {}

  private limitLabel(text: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.emojiParser.customEmojisToHtml(text));
  }

  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  get limitEntries(): { label: SafeHtml; value: number; depth: number }[] {
    const limits = this.template?.template?.limits || {};
    const entries: { label: SafeHtml; value: number; depth: number }[] = [];

    // General limits first (highest hierarchy).
    for (const key of GENERAL_LIMIT_ORDER) {
      const value = Number(limits[key]) || 0;
      if (value > 0) {
        entries.push({ label: this.limitLabel(GENERAL_LIMIT_LABELS[key]), value, depth: 0 });
      }
    }

    // Field limits next, parents before their sub-fields (Tank before Tank › Magicka).
    const emojies = this.template?.template?.emojies || {};
    for (const emojiValue of Object.values(emojies)) {
      if (typeof emojiValue !== 'string') {
        continue;
      }
      const parts = emojiValue.split(',');
      if (parts.length < 2 || (parts[0] !== 'role' && parts[0] !== 'status')) {
        continue;
      }
      const fieldKey = `${parts[0]},${parts[1]}`;
      const fieldLimit = Number(limits[fieldKey]) || 0;
      if (fieldLimit > 0) {
        entries.push({ label: this.limitLabel(parts[1]), value: fieldLimit, depth: 0 });
      }

      const nested = emojies[emojiValue];
      if (!nested || typeof nested !== 'object') {
        continue;
      }
      for (const subName of Object.values(nested)) {
        if (typeof subName !== 'string') {
          continue;
        }
        const subKey = `${parts[0]},${parts[1]},${subName}`;
        const subLimit = Number(limits[subKey]) || 0;
        if (subLimit > 0) {
          entries.push({
            label: this.limitLabel(`${parts[1]} › ${subName}`),
            value: subLimit,
            depth: fieldLimit > 0 ? 1 : 0,
          });
        }
      }
    }

    return entries;
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (this.isLoggedIn) {
      this.botService.getSession().subscribe({
        next: (session) => {
          this.isBotOwner = !!session.is_website_bot_owner;
          this.currentUserId = session.id || null;
        },
        error: () => {
          this.isBotOwner = false;
          this.currentUserId = null;
        },
      });
    }
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/templates']);
      return;
    }
    this.botService.getCatalogueTemplate(id).subscribe({
      next: (template) => {
        this.template = template;
        this.loading = false;
        if (template.template) {
          this.previewService.renderPreview(template.template, template.title);
        }
      },
      error: () => {
        this.loading = false;
        this.router.navigate(['/templates']);
      },
    });
  }

  ngOnDestroy(): void {
    this.embedService.resetEmbed();
  }

  vote(kind: 'like' | 'dislike'): void {
    if (!this.isLoggedIn) {
      this.oauthService.initLoginFlow();
      return;
    }
    if (!this.template) {
      return;
    }
    this.botService.voteCatalogueTemplate(this.template.id, kind).subscribe({
      next: (updated) => {
        this.template = { ...this.template, ...updated, template: this.template.template };
      },
    });
  }

  private canImportToGuild(guild: BotGuild): boolean {
    if (!guild.joined) {
      return false;
    }
    if (guild.owner) {
      return true;
    }
    return this.permissionsService.hasPermission(
      Number(guild.permissions_new || guild.permissions || 0),
      PermissionValue.ADMINISTRATOR
    );
  }

  openImport(): void {
    if (!this.isLoggedIn) {
      this.oauthService.initLoginFlow();
      return;
    }
    this.botService.getGuildsList().subscribe({
      next: (guilds) => {
        this.guilds = guilds.filter((g) => this.canImportToGuild(g));
        this.selectedGuildId = this.guilds[0]?.id || null;
        this.dialogRef = this.dialog.open(this.importDialog, {
          panelClass: 'raid-organizer-dialog',
          width: '420px',
        });
      },
    });
  }

  confirmImport(): void {
    if (!this.template || !this.selectedGuildId) {
      return;
    }
    this.importing = true;
    this.botService.importCatalogueTemplate(this.selectedGuildId, this.template.id).subscribe({
      next: (result) => {
        this.importing = false;
        this.dialogRef?.close();
        // The guild dashboard caches its last fetch. Drop it so the new template is in the sidebar.
        this.guildService.clearGuildData();
        this.router.navigate([
          `/guilds/${this.selectedGuildId}/signup-template/${result.id}`,
        ]);
      },
      error: () => {
        this.importing = false;
      },
    });
  }

  login(): void {
    this.oauthService.initLoginFlow();
  }

  openEdit(): void {
    if (!this.template || !this.canEditLive) {
      return;
    }
    const ref = this.dialog.open(EditCatalogueTemplateDialogComponent, {
      panelClass: 'raid-organizer-dialog',
      width: '480px',
      data: { template: this.template },
    });
    ref.afterClosed().subscribe((updated: CatalogueTemplate | undefined) => {
      this.applyUpdatedTemplate(updated);
    });
  }

  openResubmit(): void {
    if (!this.template || !this.canResubmit) {
      return;
    }
    const ref = this.dialog.open(AuthorResubmitCatalogueDialogComponent, {
      panelClass: 'raid-organizer-dialog',
      width: '520px',
      data: { template: this.template },
    });
    ref.afterClosed().subscribe((updated: CatalogueTemplate | undefined) => {
      this.applyUpdatedTemplate(updated);
    });
  }

  unlist(): void {
    if (!this.template || !this.canUnlist || this.actionBusy) {
      return;
    }
    this.actionBusy = true;
    this.botService.unlistCatalogueTemplate(this.template.id).subscribe({
      next: (updated) => {
        this.actionBusy = false;
        this.applyUpdatedTemplate(updated);
      },
      error: () => (this.actionBusy = false),
    });
  }

  relist(): void {
    if (!this.template || !this.canRelist || this.relistBlockedByAdmin || this.actionBusy) {
      return;
    }
    this.actionBusy = true;
    this.botService.relistCatalogueTemplate(this.template.id).subscribe({
      next: (updated) => {
        this.actionBusy = false;
        this.applyUpdatedTemplate(updated);
      },
      error: () => (this.actionBusy = false),
    });
  }

  deleteListing(): void {
    if (!this.template || !this.canDelete || this.actionBusy) {
      return;
    }
    const confirmed = window.confirm(
      `Permanently delete "${this.template.title}" from the catalogue? This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }
    const id = this.template.id;
    this.actionBusy = true;
    this.botService.deleteCatalogueTemplate(id).subscribe({
      next: () => {
        this.actionBusy = false;
        this.router.navigate([this.isAuthor ? '/templates/mine' : '/templates']);
      },
      error: () => (this.actionBusy = false),
    });
  }

  private applyUpdatedTemplate(updated: CatalogueTemplate | undefined): void {
    if (!updated || !this.template) {
      return;
    }
    this.template = {
      ...this.template,
      ...updated,
      template: updated.template || this.template.template,
    };
    if (this.template.template) {
      this.previewService.renderPreview(this.template.template, this.template.title);
    }
  }

  goBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }
    this.router.navigate(['/templates']);
  }
}
